import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Checkbox,
  Paper,
  IconButton,
  CircularProgress,
  LinearProgress,
  Button,
  Snackbar,
  Alert,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ChevronLeft,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import strategyService from '../../services/AGTUps-product-services/strategyService';

// Componente que define el contenedor de la estrategia
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? '#272729' : '#FFFFFF',
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.mode === 'dark' ? '#464649' : '#E5E5E5'}`
}));

// Componente que define el contenedor de cada paso
const StepItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(0, 0, 0, 0.02)',
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.04)'
  }
}));

// Componente para mostrar el estatus con un fondo de color
const StatusBadge = styled(Box)(({ color }) => ({
  padding: '4px 12px',
  borderRadius: '12px',
  backgroundColor: `${color}15`, // Color con 15% de opacidad
  color: color,
  fontSize: '0.75rem',
  fontWeight: 500
}));

/**
 * Componente ActionPlan
 * 
 * Muestra todas las estrategias, con detalles de pasos,
 * progreso y permite marcar los pasos como completados.
 * 
 * Props:
 * - onBack: función para volver a la vista anterior (Strategy overview).
 */
const ActionPlan = ({ onBack }) => {
  const theme = useTheme();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Al montar el componente, obtenemos la lista de estrategias
  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        const response = await strategyService.getUserStrategies();
        if (response.success) {
          setStrategies(response.data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStrategies();
  }, []);

  /**
   * Marca o desmarca un paso como completado.
   * - strategyId: ID de la estrategia
   * - stepId: ID del paso
   * - isCompleted: booleano indicando si el paso se está completando o descompletando
   */
  const handleStepToggle = async (strategyId, stepId, isCompleted) => {
    try {
      // Buscamos la estrategia en el estado local
      const strategy = strategies.find(s => s.strategyId === strategyId);
      if (!strategy) {
        setError('Estrategia no encontrada');
        return;
      }
  
      // Crear una copia del array de pasos y actualizar el estado del paso específico
      const updatedSteps = strategy.steps.map(step => ({
        ...step,
        completed: step.stepId === stepId ? isCompleted : step.completed
      }));
  
      // Obtener los IDs de todos los pasos completados
      const completedStepIds = updatedSteps
        .filter(step => step.completed)
        .map(step => step.stepId);
  
      // Calcular el nuevo progreso
      const progress = Math.round((completedStepIds.length / strategy.steps.length) * 100);
  
      // Determinar el nuevo estado basado en el progreso
      let newStatus;
      if (progress === 100) {
        newStatus = 'COMPLETED';
      } else if (progress > 0) {
        newStatus = 'IN_PROGRESS';
      } else {
        newStatus = 'PLANNED';
      }
  
      console.log('Actualizando estrategia:', {
        strategyId,
        completedStepIds,
        progress,
        newStatus
      });
  
      // Llamada al servicio para actualizar el progreso
      const response = await strategyService.updateProgress(
        strategyId,
        completedStepIds
      );
  
      if (response.success) {
        // Actualizar el estado local con la respuesta del servidor
        setStrategies(prevStrategies =>
          prevStrategies.map(s =>
            s.strategyId === strategyId
              ? {
                  ...s,
                  steps: updatedSteps,
                  progress,
                  status: newStatus,
                  lastModifiedAt: new Date().toISOString()
                }
              : s
          )
        );
  
        // Mostrar mensaje de éxito si es necesario
        if (progress === 100) {
          setError('¡Estrategia completada exitosamente!');
        }
      }
    } catch (err) {
      console.error('Error al actualizar el progreso:', err);
      setError(err.message || 'Error al actualizar el progreso');
    }
  };

  /**
   * Devuelve el color asociado a un estado de estrategia
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return '#2DD257'; // Verde
      case 'IN_PROGRESS':
        return '#0385FF'; // Azul
      default:
        return '#F97316'; // Naranja
    }
  };

  /**
   * Devuelve el ícono asociado a un estado de estrategia
   */
  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle size={20} color="#2DD257" />;
      case 'IN_PROGRESS':
        return <Clock size={20} color="#0385FF" />;
      default:
        return <AlertCircle size={20} color="#F97316" />;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header con botón para volver */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
            Action Plan
          </Typography>
        </Box>

        {loading ? (
          // Muestra un loader si aún estamos cargando datos
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : strategies.length === 0 ? (
          // Si no hay estrategias, invitamos a generar un análisis
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No strategies found. Generate an analysis to get started.
            </Typography>
            <Button
              variant="contained"
              onClick={async () => {
                setLoading(true);
                try {
                  const response = await strategyService.generateAnalysis();
                  if (response.success) {
                    // Si se genera correctamente, actualizamos la lista de estrategias
                    setStrategies(response.data);
                  }
                } catch (err) {
                  setError(err.message);
                } finally {
                  setLoading(false);
                }
              }}
              sx={{
                mt: 2,
                backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE',
                color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : '#0056b3',
                  boxShadow: 'none'
                }
              }}
            >
              Generate Analysis
            </Button>
          </Box>
        ) : (
          // Listado de estrategias
          strategies.map((strategy) => (
            <StyledPaper key={strategy.strategyId} elevation={0}>
              {/* Encabezado de cada estrategia */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getStatusIcon(strategy.status)}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {strategy.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {strategy.description}
                    </Typography>
                  </Box>
                </Box>
                <StatusBadge color={getStatusColor(strategy.status)}>
                  {strategy.status.replace('_', ' ')}
                </StatusBadge>
              </Box>

              {/* Barra de progreso */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {strategy.progress}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={strategy.progress || 0}
                  sx={{
                    backgroundColor: `${getStatusColor(strategy.status)}15`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getStatusColor(strategy.status)
                    }
                  }}
                />
              </Box>

              {/* Lista de pasos */}
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Action Steps
              </Typography>
              {strategy.steps.map((step) => (
                <StepItem key={step.stepId}>
                  <Checkbox
                    checked={step.completed}
                    onChange={(e) =>
                      handleStepToggle(
                        strategy.strategyId,
                        step.stepId,
                        e.target.checked
                      )
                    }
                    sx={{ mr: 2 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        textDecoration: step.completed ? 'line-through' : 'none',
                        color: step.completed ? 'text.secondary' : 'text.primary'
                      }}
                    >
                      {step.description}
                    </Typography>
                    {step.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {step.notes}
                      </Typography>
                    )}
                  </Box>
                </StepItem>
              ))}

              {/* Información adicional: métricas, recursos y riesgos */}
              {(strategy.metrics || strategy.resources || strategy.risks) && (
                <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                  {strategy.metrics && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Success Metrics
                      </Typography>
                      {Object.entries(strategy.metrics).map(([key, value]) => (
                        <Typography key={key} variant="body2" color="text.secondary">
                          • {key}: {value}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {strategy.resources && strategy.resources.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Required Resources
                      </Typography>
                      {strategy.resources.map((resource, index) => (
                        <Typography key={index} variant="body2" color="text.secondary">
                          • {resource}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {strategy.risks && strategy.risks.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Potential Risks
                      </Typography>
                      {strategy.risks.map((risk, index) => (
                        <Typography key={index} variant="body2" color="text.secondary">
                          • {risk}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </StyledPaper>
          ))
        )}

        {/* Snackbar de error */}
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default ActionPlan;
